import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, slicedToArray as _slicedToArray } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, useState, useRef } from 'react';
import { createPolymorphicComponent, useMantineTheme, Input as Input$1, Box } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';
import { isString, GUI_GLOBAL_CLASS, GUI_COMP_CSS_VARIABLE_CLASS } from '../../utils/index.js';
import { useInputStyle, useInputDynamicStyle, useInputWrapperStyle, useInputGroupStyle } from './Input.style.js';
import { Icon } from '../Icon/Icon.js';
import { NumberInput } from './NumberInput.js';

var _excluded = ["fz", "fw", "type", "invalid", "size", "styles", "className", "classNames", "htmlType", "breakpoints", "isH5MappingAppStyle", "applyColorVariable", "onClear"],
  _excluded2 = ["styles", "classNames", "className", "breakpoints", "applyColorVariable", "size"],
  _excluded3 = ["type", "size", "invalid", "disabled", "classNames", "styles", "className", "style", "children", "applyColorVariable"];
var _Input = /*#__PURE__*/forwardRef(function (props, ref) {
  var theme = useMantineTheme();
  var fz = props.fz,
    fw = props.fw,
    _props$type = props.type,
    type = _props$type === void 0 ? 'outlined' : _props$type,
    invalid = props.invalid,
    _props$size = props.size,
    size = _props$size === void 0 ? 'md' : _props$size,
    _props$styles = props.styles,
    styles = _props$styles === void 0 ? {} : _props$styles,
    _props$className = props.className,
    className = _props$className === void 0 ? '' : _props$className,
    classNames = props.classNames,
    htmlType = props.htmlType,
    _props$breakpoints = props.breakpoints,
    breakpoints = _props$breakpoints === void 0 ? [] : _props$breakpoints,
    _props$isH5MappingApp = props.isH5MappingAppStyle,
    isH5MappingAppStyle = _props$isH5MappingApp === void 0 ? theme.other.isH5MappingAppStyle : _props$isH5MappingApp,
    _props$applyColorVari = props.applyColorVariable,
    applyColorVariable = _props$applyColorVari === void 0 ? false : _props$applyColorVari,
    onClear = props.onClear,
    restProps = _objectWithoutProperties(props, _excluded);
  var hasLeft = !!restProps.icon;
  var hasRight = !!restProps.rightSection;
  var hasClear = !!onClear;
  var _useInputStyle = useInputStyle({
      type: type,
      size: size,
      hasLeft: hasLeft,
      hasRight: hasRight,
      hasClear: hasClear,
      breakpoints: breakpoints,
      isH5MappingAppStyle: isH5MappingAppStyle
    }, {
      styles: styles,
      classNames: classNames,
      name: 'GateInput'
    }),
    cx = _useInputStyle.cx,
    classes = _useInputStyle.classes;
  if (hasLeft) {
    if (isString(restProps.icon) && !!restProps.icon.match(/^icon-/)) {
      var iconElement = /*#__PURE__*/React__default.createElement(Icon, {
        icon: restProps.icon,
        className: cx(restProps.disabled && classes.disabled, classes.iconElement)
      });
      restProps.icon = iconElement;
    } else {
      restProps.icon = React__default.Children.map(restProps.icon, function (child) {
        if ( /*#__PURE__*/React__default.isValidElement(child) && child.type === Icon) {
          return /*#__PURE__*/React__default.cloneElement(child, {
            className: cx(restProps.disabled && classes.disabled, classes.iconElement, child.props.className)
          });
        }
        return child;
      });
    }
  }
  var handleOnClear = function handleOnClear(e) {
    e.preventDefault();
    if (onClear) onClear();
  };
  return /*#__PURE__*/React__default.createElement(Input$1, _objectSpread2(_objectSpread2({
    invalid: invalid
  }, restProps), {}, {
    style: _objectSpread2(_objectSpread2({}, useInputDynamicStyle({
      fz: fz,
      fw: fw
    })), restProps.style),
    rightSection: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, hasClear && ( /*#__PURE__*/React__default.createElement("svg", {
      onMouseDown: function onMouseDown(e) {
        return e.preventDefault();
      },
      onTouchStart: function onTouchStart(e) {
        return e.preventDefault();
      },
      onClick: handleOnClear,
      viewBox: "0 0 20 20",
      fill: "none",
      className: classes.clear
    }, /*#__PURE__*/React__default.createElement("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM7.56712 6.71859C7.33281 6.48428 6.95291 6.48428 6.71859 6.71859C6.48428 6.95291 6.48428 7.33281 6.71859 7.56712L9.15147 10L6.71859 12.4329C6.48428 12.6672 6.48428 13.0471 6.71859 13.2814C6.95291 13.5157 7.33281 13.5157 7.56712 13.2814L10 10.8485L12.4329 13.2814C12.6672 13.5157 13.0471 13.5157 13.2814 13.2814C13.5157 13.0471 13.5157 12.6672 13.2814 12.4329L10.8485 10L13.2814 7.56712C13.5157 7.33281 13.5157 6.95291 13.2814 6.71859C13.0471 6.48428 12.6672 6.48428 12.4329 6.71859L10 9.15147L7.56712 6.71859Z",
      fill: "currentColor"
    }))), restProps.rightSection),
    placeholder: hasClear ? restProps.placeholder || ' ' : restProps.placeholder,
    type: htmlType,
    ref: ref,
    className: cx(className, GUI_GLOBAL_CLASS, applyColorVariable ? GUI_COMP_CSS_VARIABLE_CLASS : undefined),
    classNames: classes
  }), props.children);
});
var InputWrapper = function InputWrapper(props) {
  var theme = useMantineTheme();
  var _props$styles2 = props.styles,
    styles = _props$styles2 === void 0 ? {} : _props$styles2,
    _props$classNames = props.classNames,
    classNames = _props$classNames === void 0 ? {} : _props$classNames,
    _props$className2 = props.className,
    className = _props$className2 === void 0 ? '' : _props$className2,
    _props$breakpoints2 = props.breakpoints,
    breakpoints = _props$breakpoints2 === void 0 ? [] : _props$breakpoints2,
    _props$applyColorVari2 = props.applyColorVariable,
    applyColorVariable = _props$applyColorVari2 === void 0 ? false : _props$applyColorVari2,
    _props$size2 = props.size,
    size = _props$size2 === void 0 ? 'md' : _props$size2,
    restProps = _objectWithoutProperties(props, _excluded2);
  var _useInputWrapperStyle = useInputWrapperStyle({
      breakpoints: breakpoints,
      size: size
    }, {
      styles: styles,
      classNames: classNames,
      name: 'GateInputWrapper'
    }),
    cx = _useInputWrapperStyle.cx,
    classes = _useInputWrapperStyle.classes;
  return /*#__PURE__*/React__default.createElement(Input$1.Wrapper, _objectSpread2(_objectSpread2({
    dir: theme.dir,
    inputWrapperOrder: ['label', 'input', 'description', 'error']
  }, restProps), {}, {
    className: cx(className, GUI_GLOBAL_CLASS, applyColorVariable ? GUI_COMP_CSS_VARIABLE_CLASS : undefined),
    classNames: classes
  }));
};
var InputGroup = function InputGroup(props) {
  var _props$type2 = props.type,
    type = _props$type2 === void 0 ? 'outlined' : _props$type2,
    _props$size3 = props.size,
    size = _props$size3 === void 0 ? 'md' : _props$size3,
    invalid = props.invalid,
    disabled = props.disabled,
    classNames = props.classNames,
    styles = props.styles,
    _props$className3 = props.className,
    className = _props$className3 === void 0 ? '' : _props$className3,
    style = props.style,
    children = props.children,
    _props$applyColorVari3 = props.applyColorVariable,
    applyColorVariable = _props$applyColorVari3 === void 0 ? false : _props$applyColorVari3,
    restProps = _objectWithoutProperties(props, _excluded3);
  var theme = useMantineTheme();
  var _useInputGroupStyle = useInputGroupStyle({
      type: type,
      size: size
    }, {
      classNames: classNames,
      styles: styles,
      name: 'GateInputGroup'
    }),
    cx = _useInputGroupStyle.cx,
    classes = _useInputGroupStyle.classes;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    focusStatus = _useState2[0],
    setFocusStatus = _useState2[1];
  var internalRef = useRef(null);
  var handleOnFocus = function handleOnFocus() {
    setFocusStatus(true);
  };
  var handleOnBlur = function handleOnBlur() {
    setFocusStatus(false);
  };
  var handleGroupClick = function handleGroupClick() {
    if (internalRef.current) internalRef.current.focus();
  };
  var newChild = React__default.Children.map(children, function (child) {
    if ( /*#__PURE__*/React__default.isValidElement(child) && (child.type === Input || child.type === NumberInput)) {
      var _child$props$classNam, _child$props$classNam2;
      var mergedRef = child.ref ? useMergedRef(internalRef, child.ref) : internalRef;
      return /*#__PURE__*/React__default.cloneElement(child, {
        ref: mergedRef,
        type: type,
        disabled: disabled,
        invalid: invalid,
        size: child.props.size || size,
        onFocus: function onFocus(e) {
          child.props.onFocus && child.props.onFocus(e);
          handleOnFocus();
        },
        onBlur: function onBlur(e) {
          child.props.onBlur && child.props.onBlur(e);
          handleOnBlur();
        },
        classNames: _objectSpread2(_objectSpread2({}, child.props.classNames || {}), {}, {
          wrapper: cx(classes.inputWrapper, (_child$props$classNam = child.props.classNames) === null || _child$props$classNam === void 0 ? void 0 : _child$props$classNam.wrapper),
          input: cx(classes.input, (_child$props$classNam2 = child.props.classNames) === null || _child$props$classNam2 === void 0 ? void 0 : _child$props$classNam2.input)
        })
      });
    }
    return child;
  });
  return /*#__PURE__*/React__default.createElement(Box, _objectSpread2({
    dir: theme.dir,
    style: style,
    className: cx(classes.root, className, GUI_GLOBAL_CLASS, applyColorVariable ? GUI_COMP_CSS_VARIABLE_CLASS : undefined),
    "data-invalid": invalid,
    "data-focus": focusStatus,
    "data-disabled": props.disabled,
    onClick: handleGroupClick
  }, restProps), newChild);
};
_Input.Wrapper = InputWrapper;
_Input.Group = InputGroup;
var Input = createPolymorphicComponent(_Input);

export { Input, InputGroup, InputWrapper };
//# sourceMappingURL=Input.js.map
